import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function MarketingAssessment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Section 1: Current Marketing Assets
  const [assets, setAssets] = useState({
    domain_name: '',
    website: false,
    landing_pages: false,
    email_list: false,
    email_list_size: '',
    social_media: [],
    social_followers: {},
    content_marketing: [],
    paid_advertising: [],
    seo: false,
    crm_tools: '',
    analytics_tools: '',
    other_assets: ''
  });

  // Section 2: Past Marketing Success
  const [pastSuccess, setPastSuccess] = useState('');

  // Section 3: Biggest Challenge
  const [biggestChallenge, setBiggestChallenge] = useState('');

  const socialPlatforms = ['Facebook', 'Instagram', 'LinkedIn', 'Twitter/X', 'TikTok', 'YouTube', 'Pinterest'];
  const contentTypes = ['Blog', 'Videos', 'Podcasts', 'Webinars', 'Case Studies', 'Whitepapers'];
  const paidChannels = ['Google Ads', 'Facebook Ads', 'Instagram Ads', 'LinkedIn Ads', 'YouTube Ads', 'Other'];

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load saved assessment if exists
    await fetchSavedAssessment();

    setLoading(false);
  };

  const fetchSavedAssessment = async () => {
    try {
      const response = await fetch('/api/marketing/get-assessment', {
        credentials: 'include'
      });

      let loadedAssets = null;
      if (response.ok) {
        const data = await response.json();
        if (data.assets) {
          loadedAssets = data.assets;
          setAssets(data.assets);
        }
        if (data.past_success) setPastSuccess(data.past_success);
        if (data.biggest_challenge) setBiggestChallenge(data.biggest_challenge);
      }

      // If no domain_name yet, try to prefill from website_url
      if (!loadedAssets?.domain_name) {
        const websiteResponse = await fetch('/api/business/field/website_url', {
          credentials: 'include'
        });
        if (websiteResponse.ok) {
          const websiteData = await websiteResponse.json();
          if (websiteData.field_value) {
            // Extract domain from URL (e.g., "https://www.example.com/page" -> "example.com")
            try {
              const url = new URL(websiteData.field_value);
              const domain = url.hostname.replace(/^www\./, '');
              setAssets(prev => ({ ...prev, domain_name: domain }));
            } catch {
              // If URL parsing fails, just use the raw value
              setAssets(prev => ({ ...prev, domain_name: websiteData.field_value }));
            }
          }
        }
      }
      // 404 is fine - no saved data yet
    } catch (err) {
      console.log('No saved assessment found');
    }
  };

  const handleSocialMediaToggle = (platform) => {
    if (assets.social_media.includes(platform)) {
      // Remove platform
      setAssets({
        ...assets,
        social_media: assets.social_media.filter(p => p !== platform),
        social_followers: { ...assets.social_followers, [platform]: undefined }
      });
    } else {
      // Add platform
      setAssets({
        ...assets,
        social_media: [...assets.social_media, platform]
      });
    }
  };

  const handleFollowerCount = (platform, count) => {
    setAssets({
      ...assets,
      social_followers: { ...assets.social_followers, [platform]: count }
    });
  };

  const handleContentTypeToggle = (type) => {
    if (assets.content_marketing.includes(type)) {
      setAssets({
        ...assets,
        content_marketing: assets.content_marketing.filter(t => t !== type)
      });
    } else {
      setAssets({
        ...assets,
        content_marketing: [...assets.content_marketing, type]
      });
    }
  };

  const handlePaidChannelToggle = (channel) => {
    if (assets.paid_advertising.includes(channel)) {
      setAssets({
        ...assets,
        paid_advertising: assets.paid_advertising.filter(c => c !== channel)
      });
    } else {
      setAssets({
        ...assets,
        paid_advertising: [...assets.paid_advertising, channel]
      });
    }
  };

  const handleSaveAndContinue = async () => {
    // No validation required - pre-launch businesses may have zero assets and may still be figuring out challenges

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/marketing/save-assessment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          assets,
          past_success: pastSuccess,
          biggest_challenge: biggestChallenge
        })
      });

      if (response.ok) {
        // Navigate back to dashboard (Step 2 not built yet)
        navigate('/dashboard/marketing-strategy');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save assessment');
      }
    } catch (err) {
      setError('Error saving assessment: ' + err.message);
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="marketing-strategy" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-3">Marketing Assessment</h1>
              <p className="text-center text-muted mb-4">
                Let's understand your current marketing situation to build a targeted growth strategy.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}

              {/* Section 1: Current Marketing Assets */}
              <div className="mb-5">
                <h3 className="mb-3">Current Marketing Assets</h3>
                <p className="text-muted mb-4">What marketing tools and channels do you currently have?</p>

                {/* Domain Name */}
                <div className="mb-4">
                  <Form.Group>
                    <Form.Label><strong>Domain Name</strong></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., yourbusiness.com or mybrand.io"
                      value={assets.domain_name}
                      onChange={(e) => setAssets({ ...assets, domain_name: e.target.value })}
                    />
                    <Form.Text className="text-muted">
                      Enter your domain even if you haven't built a website yet. Leave blank if you don't have one.
                    </Form.Text>
                  </Form.Group>
                </div>

                {/* Website & Landing Pages */}
                <div className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Website (live site with content)"
                    checked={assets.website}
                    onChange={(e) => setAssets({ ...assets, website: e.target.checked })}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Landing Pages (separate from main website)"
                    checked={assets.landing_pages}
                    onChange={(e) => setAssets({ ...assets, landing_pages: e.target.checked })}
                  />
                </div>

                {/* Email List */}
                <div className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Email List"
                    checked={assets.email_list}
                    onChange={(e) => setAssets({ ...assets, email_list: e.target.checked })}
                  />
                  {assets.email_list && (
                    <Form.Group className="mt-2 ms-4">
                      <Form.Label>Approximate email list size</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., 1,000 or 10,000+"
                        value={assets.email_list_size}
                        onChange={(e) => setAssets({ ...assets, email_list_size: e.target.value })}
                      />
                    </Form.Group>
                  )}
                </div>

                {/* Social Media */}
                <div className="mb-3">
                  <p className="mb-2"><strong>Social Media Platforms</strong></p>
                  {socialPlatforms.map(platform => (
                    <div key={platform} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        label={platform}
                        checked={assets.social_media.includes(platform)}
                        onChange={() => handleSocialMediaToggle(platform)}
                      />
                      {assets.social_media.includes(platform) && (
                        <Form.Group className="mt-2 ms-4">
                          <Form.Label>Follower count (optional)</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., 5,000"
                            value={assets.social_followers[platform] || ''}
                            onChange={(e) => handleFollowerCount(platform, e.target.value)}
                          />
                        </Form.Group>
                      )}
                    </div>
                  ))}
                </div>

                {/* Content Marketing */}
                <div className="mb-3">
                  <p className="mb-2"><strong>Content Marketing</strong></p>
                  {contentTypes.map(type => (
                    <Form.Check
                      key={type}
                      type="checkbox"
                      label={type}
                      checked={assets.content_marketing.includes(type)}
                      onChange={() => handleContentTypeToggle(type)}
                    />
                  ))}
                </div>

                {/* Paid Advertising */}
                <div className="mb-3">
                  <p className="mb-2"><strong>Paid Advertising</strong></p>
                  {paidChannels.map(channel => (
                    <Form.Check
                      key={channel}
                      type="checkbox"
                      label={channel}
                      checked={assets.paid_advertising.includes(channel)}
                      onChange={() => handlePaidChannelToggle(channel)}
                    />
                  ))}
                </div>

                {/* SEO */}
                <div className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="SEO / Organic Search Traffic"
                    checked={assets.seo}
                    onChange={(e) => setAssets({ ...assets, seo: e.target.checked })}
                  />
                </div>

                {/* Tools */}
                <div className="mb-3">
                  <Form.Group>
                    <Form.Label>CRM / Marketing Automation Tools</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., HubSpot, Mailchimp, ActiveCampaign"
                      value={assets.crm_tools}
                      onChange={(e) => setAssets({ ...assets, crm_tools: e.target.value })}
                    />
                  </Form.Group>
                </div>

                <div className="mb-3">
                  <Form.Group>
                    <Form.Label>Analytics Tools</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Google Analytics, Hotjar, Mixpanel"
                      value={assets.analytics_tools}
                      onChange={(e) => setAssets({ ...assets, analytics_tools: e.target.value })}
                    />
                  </Form.Group>
                </div>

                <div className="mb-3">
                  <Form.Group>
                    <Form.Label>Other Marketing Assets</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Any other marketing tools, channels, or assets not listed above"
                      value={assets.other_assets}
                      onChange={(e) => setAssets({ ...assets, other_assets: e.target.value })}
                    />
                  </Form.Group>
                </div>
              </div>

              {/* Section 2: Past Marketing Success */}
              <div className="mb-5">
                <h3 className="mb-3">Past Marketing Success</h3>
                <p className="text-muted mb-3">
                  What marketing tactics have worked well for you in the past? Think about campaigns,
                  channels, or strategies that generated results (leads, sales, engagement, etc.)
                </p>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder="Example: 'Our LinkedIn posts got great engagement when we shared customer success stories. We also had good results with a referral program where we offered 20% off to both parties.'"
                    value={pastSuccess}
                    onChange={(e) => setPastSuccess(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    If nothing has worked yet, that's okay - just write "Starting from scratch" or "No significant wins yet"
                  </Form.Text>
                </Form.Group>
              </div>

              {/* Section 3: Biggest Challenge */}
              <div className="mb-4">
                <h3 className="mb-3">Biggest Marketing Challenge</h3>
                <p className="text-muted mb-3">
                  What is the single biggest obstacle preventing your marketing from being more effective?
                </p>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Example: 'I don't have enough time to create consistent content' or 'I'm not sure which marketing channels to prioritize' or 'I struggle to convert website visitors into customers'"
                    value={biggestChallenge}
                    onChange={(e) => setBiggestChallenge(e.target.value)}
                  />
                </Form.Group>
              </div>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/marketing-strategy')}
                >
                  ← Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  disabled={saving}
                  onClick={handleSaveAndContinue}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save & Continue →'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default MarketingAssessment;
